import { Message } from "element-ui";
import OperatorService from "@/api/services/operator.service";
import {keysLower} from "@/core/helpers";

const CamerasService = {
  getList(params = { search: "", page: 1, count: 10 }) {
    return OperatorService.get(`/cameras`, { headers: params });
  },

  getStatistics() {
    return OperatorService.get(`/cameras/statistics`);
  },

  create(newCamera) {
    return OperatorService.post(`/cameras`, newCamera);
  },

  get(id) {
    return OperatorService.get(`/cameras/${id}`);
  },

  update(id, newCamera) {
    return OperatorService.put(`/cameras/${id}`, newCamera);
  },

  delete(id) {
    return OperatorService.delete(`/cameras/${id}`);
  },

  getStream(id) {
    return OperatorService.get(`/cameras/${id}/stream`);
  },

  getGreyRtspLink(id) {
    return OperatorService.get(`/cameras/${id}/grey_rtsp_link`);
  },

  getCredentialsOnvif(id) {
    return OperatorService.get(`/cameras/${id}/credentials_onvif`);
  },

  getCredentials(id) {
    return OperatorService.get(`/cameras/${id}/credentials`);
  },

  getPreviewPath(id) {
    return OperatorService.get(`/cameras/${id}/preview_path`);
  },

  getPublishUrls(id) {
    return OperatorService.get(`/cameras/${id}/publish_urls`);
  },

  getStreamsData(id) {
    return OperatorService.get(`/cameras/${id}/streams_data`);
  },

  getParameters() {
    return OperatorService.get(`/cameras/parameters`);
  },

  updateArchiveDepth(id, archive_depth) {
    return OperatorService.put(`/cameras/${id}/archive-depth`, { archive_depth });
  },

  updateTariff(id, id_tariff) {
    return OperatorService.put(`/cameras/${id}/tariff`, { id_tariff });
  },

  updateDvrServer(id, id_dvr_server, force) {
    return OperatorService.put(`/cameras/${id}/dvr-server`, { id_dvr_server, force });
  },

  updateProxyServer(id, id_proxy_server) {
    return OperatorService.put(`/cameras/${id}/proxy-server`, { id_proxy_server });
  },

  migrateMediaCore(id, id_server, id_proxy_server) {
    return OperatorService.put(`/cameras/${id}/migrate-media-core`, { id_server, id_proxy_server });
  },

  updateCameraOnvifDatetime(id, hour, minute, second, year, month, day, timezone){
    return OperatorService.put(`/cameras/${id}/datetime`, { hour, minute, second, year, month, day, timezone }).then((resp) => keysLower(resp.data));
  },

  switchState(id, state) {
    return OperatorService.put(`/cameras/${id}/state`, { state });
  },

  rebootCamera(id) {
    return OperatorService.put(`/cameras/${id}/reboot`, {});
  },

  deleteCameras(cameras) {
    return OperatorService.delete(`/cameras`, {
      cameras
    });
  },

  updateCamerasOwner(id_account_master, cameras) {
    return OperatorService.put(`/cameras/owner`, {
      cameras,
      id_account_master,
    });
  },

  updateCamerasDepth(archive_depth, cameras) {
    return OperatorService.put(`/cameras/archive`, {
      cameras,
      archive_depth,
    });
  },

  updateCamerasTariff(id_tariff, cameras) {
    return OperatorService.put(`/cameras/tariff`, {
      cameras,
      id_tariff,
    });
  },

  updateCamerasDvrServer(id_dvr_server, cameras, force) {
    return OperatorService.put(`/cameras/dvr-server`, {
      cameras,
      id_server: id_dvr_server,
      force,
    });
  },

  updateCamerasState(state, cameras) {
    return OperatorService.put(`/cameras/state`, {
      cameras,
      state: state ? "active" : "not_active",
    });
  },

  getRecipients(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/recipients`);
  },

  getCameraSharesHistory(id_camera, { page = 1, count = 10 } = {}) {
    return OperatorService.get(`/cameras/${id_camera}/shares/history`, {
      headers: {
        page,
        count,
      },
    });
  },

  addRecipient(id_camera, { id_recipient, is_access_archive, is_access_sound, is_notify_by_push, is_notify_by_telegram }) {
    return OperatorService.post(`/cameras/${id_camera}/recipients`, {
      id_recipient,
      is_access_archive: +is_access_archive,
      is_access_sound: +is_access_sound,
      is_notify_by_push: +is_notify_by_push,
      is_notify_by_telegram: +is_notify_by_telegram,
    }).catch((e) => {
      Message.error(e.response.data.error || "Произошла ошибка при добавлении доступа");
      throw e;
    });
  },

  updateRecipient(id_camera,
                  { id_camera_share, is_access_archive = false, is_access_sound = false, is_notify_by_push = false, is_notify_by_telegram = false }) {
    return OperatorService.put(`/cameras/${id_camera}/recipients`, {
      id_camera_share,
      is_access_archive: +is_access_archive,
      is_access_sound: +is_access_sound,
      is_notify_by_push: +is_notify_by_push,
      is_notify_by_telegram: +is_notify_by_telegram,
    }).catch((e) => {
      Message.error(e.response.data.error || "Произошла ошибка при обновлении доступа");
      throw e;
    });
  },

  deleteRecipient(id_camera, id_camera_share) {
    return OperatorService.delete(`/cameras/${id_camera}/recipients/${id_camera_share}`).catch((e) => {
      Message.error(e.response.data.error || "Произошла ошибка при удалении доступа");
      throw e;
    });
  },

  getConfig(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/config`);
  },

  getAuthKey(id_camera, type) {
    return OperatorService.get(`/cameras/${id_camera}/acs/keys/${type}`);
  },

  resetConfig(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/acs/reset`);
  },

  updateConfig(id_camera, config) {
    return OperatorService.post(`/cameras/${id_camera}/config`, { ...config });
  },

  getTimelinesArchive(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/timelines/archive`);
  },

  getTimelinesMotion(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/timelines/motion`);
  },

  getServersHistory(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/servers-history`);
  },

  getCameraAgentStatus(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/agent-status`);
  },

  getCameraStreamsUrls(id_camera) {
    return OperatorService.get(`/cameras/${id_camera}/camera-streams-urls`);
  },

  getConfigParameters() {
    return OperatorService.get(`/cameras/config-parameters`);
  },

  getCameraParameters(id) {
    return OperatorService.get(`/cameras/${id}/parameters`);
  },

  setCameraParameters(id, newParameters) {
    return OperatorService.put(`/cameras/${id}/parameters`, newParameters);
  },

  deleteCameraParameters(id) {
    return OperatorService.delete(`/cameras/${id}/parameters`);
  },

  getInstallationLocations() {
    return OperatorService.get(`/cameras/installation-locations`);
  },

  getMotionDetectionInstances() {
    return OperatorService.get(`/motion-detection-instances`);
  },
};

export default CamerasService;
